export type RasterizedSvg = {
  // Caller MUST revoke this via URL.revokeObjectURL when done — otherwise
  // the blob stays resident until the tab closes.
  url: string;
  blob: Blob;
  // Natural CSS px dimensions. The PNG backing is rendered at
  // `DEFAULT_PIXEL_DENSITY × these` for print sharpness.
  width: number;
  height: number;
};

// 2× is the sweet spot. 3× caused minutes-long Chrome print-preview waits
// on 800+ row reports because the compositor has 2.25× more pixels to
// composite. 2× keeps preview snappy at the cost of mild print softness.
const DEFAULT_PIXEL_DENSITY = 2;

/**
 * Rasterizes an SVG to a PNG blob URL via the canvas pipeline.
 * `toBlob` over `toDataURL`: raw binary in the blob store (often off-heap)
 * vs. ~200 KB base64 strings retained in the JS heap per row, which
 * produced ~160 MB of GC pressure on 800-row reports.
 */
export async function rasterizeSvg(
  svgElement: SVGSVGElement,
  pixelDensity: number = DEFAULT_PIXEL_DENSITY
): Promise<RasterizedSvg> {
  const serialized = new XMLSerializer().serializeToString(svgElement);

  // Some browsers require xmlns explicitly on the root when loading via
  // Blob URL.
  const withXmlns = serialized.includes('xmlns="http://www.w3.org/2000/svg"')
    ? serialized
    : serialized.replace(/<svg\b/, '<svg xmlns="http://www.w3.org/2000/svg"');

  const viewBox = svgElement.viewBox.baseVal;
  const width = viewBox.width || svgElement.clientWidth || 800;
  const height = viewBox.height || svgElement.clientHeight || 200;

  const svgBlob = new Blob([withXmlns], {
    type: "image/svg+xml;charset=utf-8"
  });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const img = new Image();
    img.src = svgUrl;
    await img.decode();

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(width * pixelDensity);
    canvas.height = Math.ceil(height * pixelDensity);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2d context unavailable");
    ctx.scale(pixelDensity, pixelDensity);
    ctx.drawImage(img, 0, 0, width, height);

    const pngBlob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
        "image/png"
      );
    });

    const pngUrl = URL.createObjectURL(pngBlob);
    return { url: pngUrl, blob: pngBlob, width, height };
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
